/**
 * @file Allow using app feature flags
 */
import { hasWwDebugUrlParam } from '@@/bits/dev_url_params'
import environment from '@@/bits/environment'
import window from '@@/bits/global'

type CurrentFlags =
  | 'banksy'
  | 'banksyDev'
  | 'dashboardAnnouncement'
  | 'debugFrontend'
  | 'joypixels8p0'
  | 'goldPlansVisible'
  | 'aiImageSearch'
  | 'realtimeFetching'
  | 'brahmsCheck'
  | 'magicPadlet'
  | 'surfaceAiChat' // keep this even though the feature is launched. This is to allow us to force AI Chat to show for some users even though they don't quality for it.
  | 'remakeSettingV2'
  | 'showCommandPalette'
  | 'publicApi'
  | 'autoContentModeration'
  | 'changeEmailVerification'
  | 'surfaceCommentsPerPost'
  | 'onboardingPanel'
  | 'galleryTemplates'
  | 'templatesWithNewSidebar'
  | 'classlinkSso'
  | 'surfaceLibraryOrgVisitorPermissions'
  | 'editThumbnail'
  | 'virtualizedDashboard'
  | 'commentModeration'
  | 'commentAutoPublishOnUpload'
  | 'googleAppLicensing'
  | 'rostering'
  | 'cleverSso'
  | 'fullClientSurface'
  | 'personalPricingPlansExperiment'
  | 'personalPricingPlansExperimentV2'
  | 'suggestedTemplates'
  | 'dashboardUpgradeCta'
  | 'surfaceRoleBadges'
  | 'remakeLink'
  | 'sandboxVisualFx'
  | 'sandboxSoundFx'
  | 'sandboxCopyPasteFrames'
  | 'virtualizedPostsGrid'
  | 'dashboardFolderDalfe'
  | 'signUpSurface'
  | 'playVideoInExpanded'
  | 'grading'
  | 'schoolLibrarySSO'
  | 'samlSetup'
  | 'removeArchive'
  | 'openInAppBanner'
  | 'demoPadletPanel'
  | 'demoPadletSignup'
  | 'onboardingDemoV2'
  | 'onboardingDemoV2Copy'
  | 'upgradeModalCopyAExperiment'
  | 'showAnonymousUserNames'
  | 'padletPickerV2'
  | 'downloadWallAttachmentsV2'
  | 'aiGrading'
  | 'upgradeModalSingleStepExperiment'
  | 'schoolLibraryPermissions'
  | 'lmsUserGroupSync'
  | 'libraryGoogleAppLicensing'
  | 'newSignupWelcomePage'
  | 'prefetchSurface'
  | 'unlimitedSandboxesV2'
  | 'currentSlideAsImageWithBanksy'
  | 'deprecateDrivePage'
  | 'wallContentProtection'
  | 'higherMonthlyPricingPlans'
  | 'padletAnalytics'
  | 'arLensesPreview'
  | 'lottieWebWorker'
  | 'sandboxCode'
  | 'classInformation'
  | 'fetchLinksWithWishes'
  | 'transferSchoolPadletPermission'
  | 'exitTicket'
  | 'featuredSection'
  | 'videoTrimming'
  | 'sandboxRetainActiveTool'
  | 'sandboxTextSizeModifier'
  | 'importPage'
  | 'wallEngagementTime'
  | 'wallEngagementTimeTracking'
  | 'translationReporter'
  | 'sandboxPngFramePreview'
  | 'librarySamlLogin'
  | 'translatePadlet'
  | 'translatePadletV2'
  | 'customPostUserProperty'
  | 'customPostVoteAndRatingProperties'
  | 'transferPadletTool'
  | 'commandBar'
  | 'yodishLanguage'
  | 'surfaceMapsV2'
  | 'cacheLoginOption'
  | 'classroomLicensesV2'
  | 'addPostLink'
  | 'editThumbnailForAudioVideoFiles'
  | 'allSlideAsImageWithBanksy'
  | 'sandboxThemeBackgrounds'
  | 'defaultDashboardLibraryNavigation'
  | 'classroomPlanPricingExperiment'
  | 'schoolLibraryOnboardingV2'
  | 'aiPermissionsForAiRecipes'
  | 'aiPermissionsForImageAndSpeechGeneration'
  | 'zapierIntegration'
  | 'sandboxTextFormatting'
  | 'undoRedoDeleteActions'
  | 'undoRedoDeleteSectionActions'
  | 'sandboxAi'
  | 'accessControlSettingsV2'
  | 'accessControlSettingsV2RetroactiveUpdate'
  | 'aiSuggestionsOnMakePage'
  | 'stripeBillingTax'
  | 'unifiedSandboxColors'
  | 'dashboardSettingsTabView'
  | 'groupPostsV2'
  | 'pdndSurfaceMatrix'
  | 'pdndSurfaceGrid'
  | 'pdndSurfaceMap'
  | 'microsoftOneDrivePicker'
  | 'googleClassroom'
  | 'googleClassroomGrading'
  | 'deviceUploads'
  | 'showInvitedLibraryUsers'
  | 'microsoftTeams'
  | 'billingAddress'
  | 'scrollSmoothnessTracking'
  | 'smartSuggestionsExperiment'
  | 'samlIdp'
  | 'librarySettingsConnectionsTab'
  | 'frontChat'
  | 'internalNavigateImmediately'
  | 'campus'
  | 'fasterDashSettings'
  | 'stripeBillingTax'
  | 'classesV1'
  | 'affiliateSignupLink'
  | 'makePageLayoutExistingUsersExperiment'
  | 'makePageLayoutNewTeachersExperiment'
  | 'delayedReactionSorting'
  | 'taxIdOnCheckout'
  | 'notifyFailedPayments'
  | 'whiteboardPreloadApis'
  | 'postComposerAttachmentPickerV2'
  | 'selfServeFreeTrial'
  | 'pdfImportButton'
  | 'userBeltY2025'
  | 'dashHeaderCtaButton'
  | 'deprecateArsPlans'
  | 'pptxImport'
  | 'posthog'
  | 'googleWorkspaceRostering'
  | 'schoolLibraryFreeze'
  | 'ltiDeepLinkingMenuV2'
  | 'surfaceTopbar'
  | 'onboardingGridLayout'
  | 'attachmentPreviewWidthAdjustmentForGridFormat'
  | 'mobileDeviceDprOverride'
  | 'reactionsToCustomPropertiesMigration'
  | 'pwaInstallButton'
  | 'wallCardMaxWidth'
  | 'cancelledLibrary'

function isAppUsing(feature: CurrentFlags, version?: null | number): boolean {
  const ww = window.ww || {}
  const viteAppFlip =
    window.wwStartingState != null && typeof window.wwStartingState === 'function' ? window.wwStartingState().flip : {}
  const flip = ww.flip || ww.vueStartingState?.flip || viteAppFlip || {}
  if (version) {
    return flip[feature] >= version
  }
  return flip[feature] || false
}

export const isDebugMode =
  (environment === 'test' && ['true', 'yes', '1'].includes(process.env.WW_DEBUG ?? '')) ||
  isAppUsing('debugFrontend') ||
  hasWwDebugUrlParam()

if (environment !== 'production' && isAppUsing('debugFrontend')) {
  console.log('Already debugging frontend in dev.') // goodcheck-disable-line
}

// If on padlet.dev, use the local Banksy server to test PDF exports.
// Banksy and Banksy Dev on Cloud Run won't work because they cannot access a padlet.dev padlet.
// You will need to run the Banksy server first before you can test PDF exports.
// See: /services/banksy/README.md
export const isAppUsingBanksyLocalDev = environment === 'development'

export { isAppUsing }
